/**
 * Звук своими силами: ни одного файла, всё синтезируется на месте.
 * Так и было решено по арту — не покупаем и не рисуем, а делаем пайплайном.
 * Здесь пайплайн — это несколько осцилляторов.
 */

type Cue = 'step' | 'coin' | 'hit' | 'fail' | 'night' | 'ui';

const MUTE_KEY = 'bratva:mute';

let context: AudioContext | null = null;
let muted = readMuted();

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function isMuted(): boolean {
  return muted;
}

export function toggleMute(): boolean {
  muted = !muted;
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    // Приватный режим: звук просто останется как есть до перезагрузки.
  }
  return muted;
}

/** Контекст создаётся лениво: до первого клика браузер его всё равно не пустит. */
function ensureContext(): AudioContext | null {
  if (muted) return null;
  if (context) return context;
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    context = new Ctor();
  } catch {
    return null;
  }
  return context;
}

interface Tone {
  type: OscillatorType;
  from: number;
  to: number;
  duration: number;
  gain: number;
}

const CUES: Record<Cue, Tone> = {
  // Шаг — короткий глухой тычок, чтобы ходьба не была беззвучной.
  step: { type: 'triangle', from: 180, to: 120, duration: 0.06, gain: 0.05 },
  // Деньги — две ноты вверх, узнаваемо и не раздражает на сотом разе.
  coin: { type: 'square', from: 660, to: 990, duration: 0.12, gain: 0.06 },
  hit: { type: 'sawtooth', from: 220, to: 90, duration: 0.16, gain: 0.09 },
  fail: { type: 'sine', from: 320, to: 140, duration: 0.22, gain: 0.07 },
  night: { type: 'sine', from: 220, to: 110, duration: 0.5, gain: 0.05 },
  ui: { type: 'square', from: 420, to: 420, duration: 0.04, gain: 0.03 },
};

export function play(cue: Cue): void {
  const ctx = ensureContext();
  if (!ctx) return;

  const tone = CUES[cue];
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = tone.type;
  osc.frequency.setValueAtTime(tone.from, now);
  osc.frequency.linearRampToValueAtTime(tone.to, now + tone.duration);

  // Мгновенная атака и плавный спад: без спада щелчки режут слух.
  gain.gain.setValueAtTime(tone.gain, now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + tone.duration);

  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + tone.duration + 0.02);
}
